import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Shield, Activity, FileLock2, Network as NetIcon, AlertTriangle, RotateCcw, ScrollText, Users as UsersIcon, Cloud, FileCheck2, Lock, FileKey, Eye, UserCog, LogOut, X, Tag, FileSearch, Sparkles, ShieldCheck, Key, Send, KeyRound, Building, Settings as Cog, Radio, Camera, ShieldAlert, Gauge, Search, Server } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const groups = [
  {
    header: "CONTROL PLANE",
    items: [
      { to: "/dashboard", label: "Overview", icon: Activity, testid: "nav-dashboard" },
      { to: "/policies", label: "Firewall Policies", icon: FileLock2, testid: "nav-policies" },
      { to: "/proxy", label: "Proxy Rules", icon: NetIcon, testid: "nav-proxy" },
      { to: "/identity-policies", label: "Identity Policies", icon: ShieldCheck, testid: "nav-ipol" },
      { to: "/threats", label: "Threat Detection", icon: AlertTriangle, testid: "nav-threats" },
      { to: "/healing", label: "Self-Healing", icon: RotateCcw, testid: "nav-healing" },
      { to: "/vpn", label: "VPN Tunnels", icon: Lock, testid: "nav-vpn" },
      { to: "/network", label: "Network Defenses", icon: Eye, testid: "nav-network" },
      { to: "/certs", label: "Certificates", icon: FileKey, testid: "nav-certs" },
      { to: "/connectors", label: "Cloud Connectors", icon: Cloud, testid: "nav-connectors" },
      { to: "/appliances", label: "Appliances", icon: Server, testid: "nav-appliances", adminOnly: true },
      { to: "/telemetry", label: "Telemetry", icon: Activity, testid: "nav-telemetry" },
    ],
  },
  {
    header: "DATA PROTECTION",
    items: [
      { to: "/labels", label: "Sensitivity Labels", icon: Tag, testid: "nav-labels" },
      { to: "/dlp", label: "DLP Rules", icon: FileSearch, testid: "nav-dlp" },
      { to: "/classifier", label: "Classifier", icon: Sparkles, testid: "nav-classifier" },
      { to: "/search-filter", label: "Search Filter", icon: Search, testid: "nav-sqf", adminOnly: true },
    ],
  },
  {
    header: "OPERATIONS",
    items: [
      { to: "/monitor", label: "Packet Monitor", icon: Radio, testid: "nav-monitor" },
      { to: "/pcap", label: "Packet Capture", icon: Camera, testid: "nav-pcap", adminOnly: true },
      { to: "/qos", label: "QoS Classes", icon: Gauge, testid: "nav-qos", adminOnly: true },
      { to: "/ephemeral", label: "Ephemeral Mode", icon: ShieldAlert, testid: "nav-ephemeral" },
    ],
  },
  {
    header: "GOVERNANCE",
    items: [
      { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit" },
      { to: "/compliance", label: "Compliance", icon: FileCheck2, testid: "nav-compliance" },
      { to: "/audit-sinks", label: "Audit Sinks", icon: Send, testid: "nav-sinks", adminOnly: true },
    ],
  },
  {
    header: "ADMIN",
    items: [
      { to: "/users", label: "User Policies", icon: UsersIcon, testid: "nav-users", adminOnly: true },
      { to: "/orgs", label: "Orgs (SCIM)", icon: Building, testid: "nav-orgs", adminOnly: true },
      { to: "/sso", label: "SSO Providers", icon: KeyRound, testid: "nav-sso", adminOnly: true },
      { to: "/kms", label: "KMS Keys", icon: Key, testid: "nav-kms", adminOnly: true },
      { to: "/settings", label: "Settings", icon: Cog, testid: "nav-settings" },
      { to: "/profile", label: "Profile", icon: UserCog, testid: "nav-profile" },
    ],
  },
];

function SidebarBody({ onItemClick, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full bg-zinc-950 border-r border-zinc-800 w-64 shrink-0">
      <div className="px-5 py-5 border-b border-zinc-800 flex items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <img src="/rustwall-logo.png" alt="Rustwall" className="h-8 w-auto object-contain" />
          <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-500">v3.2.1 · NODE 0xA1F</div>
        </div>
        {onClose && (
          <button data-testid="sidebar-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-200 lg:hidden">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3" data-testid="sidebar-nav">
        {groups.map((g, gi) => {
          const visible = g.items.filter((it) => !it.adminOnly || user?.role === "admin");
          if (visible.length === 0) return null;
          return (
            <div key={g.header} className={gi > 0 ? "mt-3" : ""}>
              <div className="px-5 pb-2 text-[9px] font-mono uppercase tracking-[0.3em] text-zinc-600">{g.header}</div>
              {visible.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={it.testid}
                    onClick={onItemClick}
                    className={({ isActive }) =>
                      `mx-2 my-0.5 px-3 py-2 flex items-center gap-3 rounded-sm text-sm transition-colors duration-150 ` +
                      (isActive
                        ? "bg-cyan-500/10 text-cyan-300 border-l-2 border-cyan-500"
                        : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border-l-2 border-transparent")
                    }
                  >
                    <Icon className="w-4 h-4" />
                    <span className="whitespace-normal break-words">{it.label}</span>
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-zinc-800 p-3 space-y-2">
        <div className="px-2 py-2 rounded-sm bg-zinc-900/60 border border-zinc-800">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Operator</div>
          <div className="text-sm text-zinc-200 whitespace-normal break-words" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest mt-0.5">
            {user?.role} · {user?.auth_provider}{user?.twofa_enabled ? " · 2FA" : ""}
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-red-400 hover:bg-zinc-900 rounded-sm transition"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  return (
    <>
      {/* Desktop */}
      <aside className="hidden lg:flex">
        <SidebarBody />
      </aside>
      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div data-testid="sidebar-backdrop" onClick={() => setMobileOpen(false)}
               className="lg:hidden fixed inset-0 bg-black/70 z-30 backdrop-blur-sm" />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-40 fade-up" data-testid="mobile-sidebar">
            <SidebarBody onItemClick={() => setMobileOpen(false)} onClose={() => setMobileOpen(false)} />
          </aside>
        </>
      )}
    </>
  );
}
